import { useEffect, useRef, useState } from 'react';

import {
  DefaultTrackPromotionEvent,
  trackG4ViewPromotion,
} from '@/common/analytics/gtm/shared/trackViewPromotion';
import useBrowser from '@/common/hooks/useBrowser/useBrowser';
import { useResponsive } from '@/common/hooks/useResponsive/useResponsive';
import { ImpressionsContextProvider } from '@/common/impressions';
import CurrentEventsView from '@/modules/HomePage/components/CurrentEvents/CurrentEventsView';
import useHomepageContext from '@/modules/HomePage/HomePageContext';

const FIRST_VISIBLE_ITEMS_ON_MOBILE = 2.5;
const MAX_ITEMS_TO_REVEAL = 3;

const CurrentEvents = () => {
  const { currentEvents: events } = useHomepageContext();
  const isBrowser = useBrowser();
  const { isMobileOrTablet } = useResponsive();

  const [visibleItems, setVisibleItems] = useState(FIRST_VISIBLE_ITEMS_ON_MOBILE);
  const [eventTileHeight, setEventTileHeight] = useState(0);
  const [listHeight, setListHeight] = useState(0);
  const [isButtonHidden, setIsButtonHidden] = useState(false);

  const listRef = useRef<HTMLUListElement | null>(null);

  const setItemsAmountToReveal = () => {
    if (events.length - visibleItems >= MAX_ITEMS_TO_REVEAL) return MAX_ITEMS_TO_REVEAL;
    return events.length - Math.floor(visibleItems);
  };
  const amountToReveal = setItemsAmountToReveal();

  const handleShowMore = () => {
    setVisibleItems((prevVisibleItems) => prevVisibleItems + amountToReveal);
  };

  useEffect(() => {
    setListHeight(isMobileOrTablet ? eventTileHeight * visibleItems : 0);
  }, [eventTileHeight, isMobileOrTablet, visibleItems]);

  useEffect(() => {
    const list = listRef.current;

    const handleTransitionEnd = () => {
      if (amountToReveal === 0) setIsButtonHidden(true);
    };

    list?.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      list?.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [amountToReveal]);

  return (
    <ImpressionsContextProvider<DefaultTrackPromotionEvent>
      callback={(data) => {
        trackG4ViewPromotion(data);
      }}
      shouldSendSeparately
    >
      <CurrentEventsView
        amountToReveal={amountToReveal}
        events={events}
        handleShowMore={handleShowMore}
        isBrowser={isBrowser}
        isButtonHidden={isButtonHidden}
        listHeight={listHeight || 'unset'}
        ref={listRef}
        setEventTileHeight={setEventTileHeight}
      />
    </ImpressionsContextProvider>
  );
};

export default CurrentEvents;
